export const APP_NAME = 'hmawiw';

/** Motivation columns, index 0 is the leftmost and least motivated. */
/** Columns by default; a user may choose between MIN_COLUMNS and MAX_COLUMNS. */
export const MOTIVATION_LEVELS = 3;
export const MIN_COLUMNS = 2;
export const MAX_COLUMNS = 6;

/** Importance bands inside a column, index 0 is the topmost and most important. */
export const BAND_COUNT = 3;

/** Energy a card requires: 1 low, 2 medium, 3 high. */
export const ENERGY_LEVELS = 3;

/** A column index, 0 being the least wanted; the user's column count bounds it. */
export type Motivation = number;
export type Band = 0 | 1 | 2;
export type Energy = 1 | 2 | 3;
export type CardStatus = 'open' | 'done';

/** Hybrid logical clock string; see hlc.ts. Lexicographic order is causal order. */
export type Clock = string;

/** Fields whose concurrent edits keep the losing value as a visible conflict. */
export const CONTENT_FIELDS = ['title', 'notes', 'firstStep', 'energy', 'dueAt', 'reminders', 'repeat', 'dread', 'status', 'deletedAt'] as const;

/** Fields merged newest-wins without a conflict row: a card must end up in exactly one place. */
export const SILENT_FIELDS = ['motivation', 'band', 'rank', 'doneAt', 'touchedAt', 'attempts', 'doneLog', 'inbox'] as const;

export const MERGE_FIELDS = [...CONTENT_FIELDS, ...SILENT_FIELDS] as const;

export type ContentField = (typeof CONTENT_FIELDS)[number];
export type SilentField = (typeof SILENT_FIELDS)[number];
export type MergeField = (typeof MERGE_FIELDS)[number];

/**
 * A reminder fires at a fixed time, or a fixed span before the due time (silent while there is
 * no due date). `ring` makes it an alarm: loud sound and vibration instead of a plain notification.
 */
export type Reminder = { kind: 'at'; at: number; ring?: boolean } | { kind: 'before'; ms: number; ring?: boolean };

/**
 * Four columns became three on 2026-09-10 (Dreading, Neutral, Eager): the two middle ones
 * merged. The same rule runs on the server and on every device's local copy, once.
 */
export function motivationFromFour(old: number): Motivation {
  return old <= 0 ? 0 : old >= 3 ? 2 : 1;
}

/**
 * A card keeps its place on the axis when the column count changes: the index scales so the
 * ends stay the ends and the middle stays near the middle.
 */
export function remapMotivation(old: number, from: number, to: number): Motivation {
  if (from <= 1 || to <= 1) return 0;
  const clamped = Math.max(0, Math.min(from - 1, old));
  return Math.round((clamped * (to - 1)) / (from - 1));
}

/** Column names stored for four columns keep the outer two; the merged middle takes the default name. */
export function columnLabelsFromFour<T>(labels: T[]): T[] {
  return labels.length === 4 ? [labels[0] as T, null as T, labels[3] as T] : labels;
}

export const MAX_REMINDERS = 8;
export const REMINDER_MAX_BEFORE_MS = 30 * 24 * 3_600_000;

/** One "give it ten minutes" session: when it started, and whether the ten minutes ran out rather than being stopped. */
export interface Attempt {
  at: number;
  done: boolean;
}

export const ATTEMPT_MS = 10 * 60_000;
export const MAX_ATTEMPTS = 50;

/**
 * How a card comes back. The three dated kinds advance the due date on Done and rest off the
 * board until `lead` days before it; `ongoing` never finishes and asks to be touched every
 * `days` days. Weekdays are ISO (1 Monday to 7 Sunday); a monthly or yearly day past the
 * month's end falls on its last day (29 February lands on the 28th in other years).
 */
export type Repeat =
  | { kind: 'after'; days: number; lead: number }
  | { kind: 'weekly'; days: number[]; lead: number }
  | { kind: 'monthly'; day: number; lead: number }
  | { kind: 'yearly'; month: number; day: number; lead: number }
  | { kind: 'ongoing'; days: number };

export type RepeatKind = Repeat['kind'];

/** Why a card is dreaded, named by the user; each reason has one suggested move. */
export const DREAD_REASONS = ['unclear', 'big', 'boring', 'scary', 'waiting'] as const;
export type DreadReason = (typeof DREAD_REASONS)[number];

export const REPEAT_MAX_DAYS = 365;
export const REPEAT_MAX_LEAD = 30;
/** Timestamps of each Done on a recurring card or Touch on an ongoing one, oldest first. */
export const MAX_DONE_LOG = 100;

export interface CardFields {
  title: string;
  notes: string | null;
  /** The smallest action that starts the card; the lever against dread. */
  firstStep: string | null;
  energy: Energy;
  dueAt: number | null;
  reminders: Reminder[];
  repeat: Repeat | null;
  dread: DreadReason | null;
  status: CardStatus;
  deletedAt: number | null;
  motivation: Motivation;
  band: Band;
  rank: number;
  doneAt: number | null;
  touchedAt: number | null;
  attempts: Attempt[];
  doneLog: number[];
  /** Added on the go and not yet given a column; the motivation and band are placeholders until sorted. */
  inbox: boolean;
}

export interface Card extends CardFields {
  id: string;
  boardId: string;
  createdAt: number;
  updatedAt: number;
  /** One clock per merged field; a missing entry means the field was never written through sync. */
  clocks: Partial<Record<MergeField, Clock>>;
}

export interface FieldPatch<T = unknown> {
  value: T;
  clock: Clock;
  /** The clock the device last synced for this field, null when it never saw one. */
  base: Clock | null;
}

export type PatchFields = { [K in MergeField]?: FieldPatch<CardFields[K]> };

export interface CardPatch {
  id: string;
  boardId?: string;
  createdAt?: number;
  fields: PatchFields;
}

export interface Conflict {
  id: string;
  cardId: string;
  field: ContentField;
  losingValue: unknown;
  /** Carries the losing device's node id, which the server turns into a device name. */
  losingClock: Clock;
  deviceName: string | null;
  createdAt: number;
}

export interface EnergyNow {
  level: Energy;
  setAt: number;
}

export interface SettingRow {
  json: string;
  clock: Clock;
}

export interface SettingPatch extends SettingRow {
  base: Clock | null;
}
